import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { useApp } from '@/context/AppContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { SectionTitle, Spinner } from '@/components/common';
import { toast } from 'sonner';
import { LogOut, Moon, Sun, KeyRound, ShieldCheck, ShieldX, RefreshCw, History } from 'lucide-react';

const fmtTs = (iso) => {
  try {
    return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
};

const LoginActivity = () => {
  const [rows, setRows] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.get('/auth/attempts?limit=25');
      setRows(data);
    } catch {
      setRows([]);
      toast.error('Could not load login activity');
    } finally { setRefreshing(false); }
  };

  useEffect(() => { load(); }, []);

  return (
    <Card data-testid="settings-login-activity-card">
      <CardContent className="p-5">
        <div className="flex items-center justify-between">
          <SectionTitle>Login Activity</SectionTitle>
          <Button data-testid="settings-login-activity-refresh" variant="ghost" size="sm" onClick={load} disabled={refreshing} className="gap-1.5 text-xs">
            <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
        {rows === null ? (
          <div className="space-y-2">
            {[0, 1, 2].map((i) => <Skeleton key={i} className="h-9 w-full" />)}
          </div>
        ) : rows.length === 0 ? (
          <div className="flex items-center gap-2 rounded-lg border border-dashed border-border px-3 py-4 text-sm text-muted-foreground">
            <History className="h-4 w-4" /> No login attempts recorded yet.
          </div>
        ) : (
          <div className="max-h-72 space-y-1 overflow-y-auto pr-1">
            {rows.map((a) => (
              <div key={a.id} data-testid="settings-login-activity-row" className="flex items-center justify-between rounded-lg px-2 py-1.5 hover:bg-accent/60">
                <div className="flex items-center gap-2.5">
                  {a.success
                    ? <ShieldCheck className="h-4 w-4 shrink-0 text-emerald-400" />
                    : <ShieldX className="h-4 w-4 shrink-0 text-red-400" />}
                  <div>
                    <p className="text-xs font-medium leading-tight">{a.success ? 'Successful login' : 'Failed attempt'}</p>
                    <p className="text-[11px] text-muted-foreground">{fmtTs(a.ts)}{a.ip ? ` · ${a.ip}` : ''}</p>
                  </div>
                </div>
                {a.locked && <Badge variant="outline" className="border-amber-500/40 text-[10px] text-amber-400">locked</Badge>}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default function Settings() {
  const { theme, setTheme, logout } = useApp();
  const [currentPin, setCurrentPin] = useState('');
  const [newPin, setNewPin] = useState('');
  const [busy, setBusy] = useState(false);

  const changePin = async () => {
    if (!/^\d{6}$/.test(newPin)) { toast.error('New PIN must be exactly 6 digits'); return; }
    setBusy(true);
    try {
      await api.post('/auth/change-pin', { current_pin: currentPin, new_pin: newPin });
      toast.success('PIN changed');
      setCurrentPin('');
      setNewPin('');
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'PIN change failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="mx-auto max-w-xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Settings</h1>
        <div className="page-accent-bar" />
        <p className="mt-2 text-sm text-muted-foreground">ChromiumHQ — your command center, your rules.</p>
      </div>

      <Card>
        <CardContent className="p-5">
          <SectionTitle>Appearance</SectionTitle>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {theme === 'dark' ? <Moon className="h-5 w-5 text-primary" /> : <Sun className="h-5 w-5 text-amber-500" />}
              <div>
                <p className="text-sm font-medium">{theme === 'dark' ? 'Mission Control (Dark)' : 'Minimal Productivity (Light)'}</p>
                <p className="text-xs text-muted-foreground">Switch between the dark command-center and clean light theme.</p>
              </div>
            </div>
            <Switch data-testid="settings-theme-switch" checked={theme === 'dark'} onCheckedChange={(v) => setTheme(v ? 'dark' : 'light')} aria-label="Dark theme" />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-5">
          <SectionTitle>Security</SectionTitle>
          <div className="space-y-3">
            <div>
              <Label>Current PIN</Label>
              <Input data-testid="settings-current-pin-input" type="password" inputMode="numeric" maxLength={6} autoComplete="off" value={currentPin} onChange={(e) => setCurrentPin(e.target.value.replace(/\D/g, ''))} className="mt-1" placeholder="••••••" />
            </div>
            <div>
              <Label>New PIN (6 digits)</Label>
              <Input data-testid="settings-new-pin-input" type="password" inputMode="numeric" maxLength={6} autoComplete="off" value={newPin} onChange={(e) => setNewPin(e.target.value.replace(/\D/g, ''))} className="mt-1" placeholder="••••••" />
            </div>
            <Button data-testid="settings-change-pin-button" onClick={changePin} disabled={busy || currentPin.length !== 6 || newPin.length !== 6} className="gap-2">
              {busy ? <Spinner /> : <KeyRound className="h-4 w-4" />} Change PIN
            </Button>
            <p className="text-[11px] text-muted-foreground">PINs are stored as salted hashes and verified server-side. 5 wrong login attempts lock the app for 5 minutes.</p>
          </div>
        </CardContent>
      </Card>

      <LoginActivity />

      <Card>
        <CardContent className="p-5">
          <SectionTitle>Session</SectionTitle>
          <p className="mb-3 text-xs text-muted-foreground">Sessions stay signed in for 24 hours, then require your PIN again.</p>
          <Button data-testid="settings-logout-button" variant="outline" onClick={logout} className="gap-2">
            <LogOut className="h-4 w-4" /> Lock & Log Out
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
